import { useEffect, useState } from 'react';
import { format } from 'date-fns';
import documentBundleService from 'src/services/apiServices/document-bundle/document-bundle-service';

interface Args {
  dateFrom: Date;
  dateTo: Date;
  payerType: string;
  payerName: string;
  dateScope: string;
  therapistId: string;
}

/**
 * SQ-171: how many documents each therapist scope would return.
 *
 * Deliberately not `useDocumentSummary`. That hook dispatches into a single shared Redux
 * slice, so calling it twice gives both callers the same value — whichever request landed
 * last. On screen that read as both sides of the toggle showing the same number and
 * tracking whichever tab was selected, which is worse than showing nothing: it looks like
 * an answer.
 *
 * These two counts are display-only, belong to one control, and are never read anywhere
 * else, so they are fetched directly and held locally.
 */
export function useTherapistScopeCounts({
  dateFrom,
  dateTo,
  payerType,
  payerName,
  dateScope,
  therapistId,
}: Args) {
  const [counts, setCounts] = useState<{ author: number | null; charts: number | null }>({
    author: null,
    charts: null,
  });

  const from = format(dateFrom, 'yyyy-MM-dd');
  const to = format(dateTo, 'yyyy-MM-dd');

  useEffect(() => {
    if (!therapistId) {
      setCounts({ author: null, charts: null });
      return undefined;
    }

    //  A slow reply for a therapist the user has already moved off must not overwrite the
    //  current one.
    let current = true;

    const fetchScope = async (scope: 'author' | 'charts') => {
      const body: Record<string, unknown> = {
        date_from: from,
        date_to: to,
        therapist_id: therapistId,
        therapist_scope: scope,
      };
      if (payerType) body.payer_type = payerType;
      if (payerName) body.payer_name = payerName;
      if (dateScope && dateScope !== 'range') body.date_scope = dateScope;

      const response = await documentBundleService.getDocumentSummary(body as any);
      return response?.data?.data?.documentCount ?? null;
    };

    Promise.all([fetchScope('author'), fetchScope('charts')])
      .then(([author, charts]) => {
        if (current) setCounts({ author, charts });
      })
      .catch(() => {
        //  The toggle still works without the numbers; it just cannot preview them.
        if (current) setCounts({ author: null, charts: null });
      });

    return () => {
      current = false;
    };
  }, [from, to, payerType, payerName, dateScope, therapistId]);

  return counts;
}
